// A blog post: the share button, the reading progress bar and the contents
// menu that follows the reader down the article.
//
// The progress bar hides the site header while the reader is inside the
// article and shows it again at the foot; because the header outlives the
// page, the teardown puts it back. The contents menu is an
// IntersectionObserver over the article's sections, disconnected on unmount.
import $ from "../jquery-setup.js";

const HEADER_OFFSET = 60; // px above a section its anchor scrolls to

export function mountArticle(context) {
  mountSharePanel(context);
  const teardowns = [mountReadingProgress(context), mountContentsMenu(context)];
  return () => teardowns.forEach((teardown) => teardown && teardown());
}

// Copies the article's URL to the clipboard.
function mountSharePanel({ root }) {
  root.querySelectorAll(".article-layout__share-btn").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();
      button.classList.toggle("article-layout__share-btn--active");
      root.querySelectorAll(".article-layout__share-btn-icon").forEach((icon) => icon.classList.toggle("article-layout__share-btn-icon--hide"));
      navigator.clipboard?.writeText(location.href).catch(() => {});
    });
  });
  root.querySelector(".article-layout__comment-notification")?.addEventListener("click", (event) => {
    event.preventDefault();
    const comments = root.querySelector(".article-layout__comments-module");
    if (comments) window.scrollTo({ top: comments.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET });
  });
}

function mountReadingProgress({ root, signal }) {
  const container = root.querySelector(".reading-progress__container");
  const body = root.querySelector(".article-layout__article-body-col");
  const start = root.querySelector(".reading-progress-start");
  if (!container || !body || !start) return;
  const bar = container.querySelector(".reading-progress__progress-bar");
  const header = document.querySelector(".nav__holder");

  window.addEventListener(
    "scroll",
    () => {
      const footer = root.querySelector(".gc-footer");
      const footerTop = footer ? footer.offsetTop - footer.offsetHeight : Infinity;
      const scrolled = window.scrollY - start.offsetTop;
      const percent = Math.min(100, Math.round((scrolled / body.offsetHeight) * 100));
      const reading = window.scrollY > start.offsetTop && window.scrollY < footerTop;
      container.classList.toggle("show", reading);
      if (header) header.style.transform = reading ? "translateY(-100%)" : "translateY(0)";
      if (bar) bar.style.width = `${percent}%`;
    },
    { passive: true, signal },
  );

  return () => {
    if (header) header.style.transform = "";
  };
}

// Highlights the contents link of the section in view.
function mountContentsMenu({ root }) {
  const sections = [...root.querySelectorAll(".article-layout__article-body-col section[id]")];
  if (!sections.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const link = root.querySelector(`a[href="#${entry.target.id}"]`);
        link?.classList.toggle("active", entry.intersectionRatio > 0);
      });
    },
    { rootMargin: "-49% 0px", threshold: 0 },
  );
  sections.forEach((section) => observer.observe(section));

  root.querySelectorAll('a[href*="#"]').forEach((link) => {
    link.addEventListener("click", (event) => {
      const target = root.querySelector(link.getAttribute("href"));
      if (!target) return;
      event.preventDefault();
      window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET });
    });
  });

  return () => observer.disconnect();
}
